import { useQuery } from "@tanstack/react-query";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationListStandalone, PaginationProvider, PaginationTotalStandalone, SizePerPageDropdownStandalone } from "react-bootstrap-table2-paginator";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import ControlService from "src/modules/data-protection/apis/ControlService";
import overlayFactory from "react-bootstrap-table2-overlay";
import DateUtils from "src/services/utils/DateUtils";
import { memo, useMemo, useState } from "react";
import { connect } from "react-redux";
import SOAPdfExport from "../export/PDF";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";

const ExportWaitingModal = memo(({ t, isOpen, toggle, pdfObjectUrl, version }) => {
    const openFileInNewTab = () => {
        window.open(pdfObjectUrl, '_blank');
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                <div>
                    {!pdfObjectUrl ? (
                        <Spinner
                            className="me-2"
                            animation="border"
                            variant="info"
                            size="sm"
                        />
                    ) : null}
                    {t(!pdfObjectUrl ? 'Downloading SoA Version...' : 'Download Complete')}
                </div>
            </ModalHeader>

            <ModalBody>
                {pdfObjectUrl ? (
                    <>
                        <p>
                            {t('SoA version {{version}} has been successfully downloaded.', { version: version })}
                        </p>
                        <Button 
                            color="secondary"
                            onClick={openFileInNewTab}
                            className="d-flex align-items-center"
                            size="sm"
                        >
                            <i className="ri-external-link-line font-size-20 me-1" />
                            {t('Open PDF in New Tab')}
                        </Button>
                    </>
                ) : (
                    <>
                        {t('Your download for SoA version {{version}} is in progress. Please wait.', { version: version })}..
                    </>
                )}
            </ModalBody>

            <ModalFooter>
                <Button onClick={toggle} color="secondary" outline>
                    {t('Close')}
                </Button>
            </ModalFooter>
        </Modal>
    )
})

const ListVersions = ({ 
    t, 
    user, 
    organization,
    controlType
}) => {
    const [ versionToDownload, setVersionToDownload ] = useState(null);

    useQuery({
        queryKey: [
            "data-protection-controls-list-for-export",
            versionToDownload?.id
        ],
        queryFn: async () => {
            const service = ControlService.getInstance();
            return await service.fetchVersion(versionToDownload.id);
        },
        refetchOnWindowFocus: false,
        enabled: !!versionToDownload?.id,
        onSuccess: async (response) => {
            const {
                controls_snapshot
            } = response
            let fileName = `Statement of Applicability v-${versionToDownload.version}.pdf`;

            const blob = await pdf(
                <SOAPdfExport
                    controls={controls_snapshot.controls}
                    organization={organization}
                    user={user}
                />
            ).toBlob();

            saveAs(blob, fileName);

            setVersionToDownload((currentState) => {
                return {
                    ...currentState,
                    pdfObjectUrl: URL.createObjectURL(blob)
                };
            });
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("Download failed. Unable to download SoA version {{version}}. Please try again.", { version: versionToDownload?.version }), {
                type: "error"
            });

            setVersionToDownload(null);
        }
    });

    const {
        data: versions,
        isFetching: versionsAreFetching,
        isLoading: versionsAreLoading,
        isFetched: versionsAreFetched
    } = useQuery({
        queryKey: [
            "data-protection-fetch-controls-versions",
            controlType?.value
        ],
        queryFn: async () => {
            const service = ControlService.getInstance();
            return await service.fetchVersions({
                framework: controlType?.value
            });
        },
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("Failed to load SoA versions."), {
                type: "error"
            });
        }
    });

    const tableColumns = useMemo(() => {
        const dateUtils = new DateUtils();

        return [
            {
                dataField: "version",
                text: t("Version"),
                sort: true,
                key: 0,
                style: {
                    width: "200px"
                }
            },
            {
                dataField: "created_at",
                text: t("Created on"),
                sort: true,
                key: 1,
                style: {
                    width: "120px"
                },
                formatter: (cell) => {
                    return (
                        dateUtils.convertTimeStampToDate(
                            cell,
                            INTERNATIONAL_DATE_FORMAT
                        )
                    )
                }
            },
            {
                dataField: "created_by",
                text: t("Created by"),
                sort: true,
                key: 2,
                style: {
                    width: "120px"
                },
                formatter: (cell) => {
                    return cell?.full_name || "-";
                }
            },
            {
                dataField: "action",
                text: t("Actions"),
                sort: false,
                key: 3,
                style: {
                    width: "120px"
                },
                formatter: (_cell, row) => {
                    return (
                        <Button
                            color="link"
                            className="waves-effect me-1"
                            onClick={() => setVersionToDownload(row)}
                        >
                            <i className="ri-download-line font-size-22"></i>
                        </Button>
                    )
                }
            }
        ]
    }, [ t ])

    const NoDataIndication = () => {
        if (versionsAreFetched && !versions?.items?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p
                        style={{
                            textAlign: "center",
                            marginBottom: 0,
                        }}
                    >
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            );
        }

        return <></>;
    }

    return (
        <Row>
            <Col sm="12">
                <PaginationProvider
                    pagination={paginationFactory({
                        custom: true,
                        page: 1,
                        sizePerPage: 25,
                        totalSize: parseInt(versions?.items?.length || 0),
                        withFirstAndLast: false,
                        alwaysShowAllBtns: true,
                        prePageText: (
                            <span>
                                <i className="ri-arrow-left-s-line"></i> 
                                {t("Back")}
                            </span>
                        ),
                        nextPageText: (
                            <span>
                                {t("Next")} <i className="ri-arrow-right-s-line"></i>
                            </span>
                        ),
                        prePageTitle: t("Pre page"),
                        firstPageTitle: t("Next page"),
                        sizePerPageList: [
                            {
                                text: "25",
                                value: 25,
                            },
                            {
                                text: "50",
                                value: 50,
                            },
                        ],
                        showTotal: false,
                        paginationTotalRenderer: (from, to, size) => {
                            return (
                                <span className="react-bootstrap-table-pagination-total">
                                    {`${t('Total Items')} ${size}`}
                                </span>
                            )
                        },
                        hideSizePerPage: false,
                    })}
                >
                    {({ paginationProps, paginationTableProps }) => (
                        <>
                            <Row>
                                <Col sm="12">
                                    <BootstrapTable
                                        loading={ versionsAreFetching || versionsAreLoading }
                                        overlay={overlayFactory({
                                            spinner: (
                                                <Spinner
                                                    animation="border"
                                                    variant="primary"
                                                    size="md"
                                                />
                                            ),
                                            text: "Loading...",
                                        })}
                                        defaultSorted={[]}
                                        keyField={"id"}
                                        responsive
                                        bordered={false}
                                        data={versions?.items || []}
                                        striped={false}
                                        columns={tableColumns}
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table"}
                                        headerWrapperClasses={"thead-light"}
                                        style={{
                                            overflowX: "auto",
                                        }}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationTotalStandalone
                                            {...paginationProps}
                                        />

                                        <div>
                                            <PaginationListStandalone
                                                {...paginationProps}
                                            />

                                            <SizePerPageDropdownStandalone
                                                {...paginationProps}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </PaginationProvider>

                <ExportWaitingModal 
                    t={t}
                    isOpen={!!versionToDownload?.id}
                    toggle={() => setVersionToDownload(null)}
                    pdfObjectUrl={versionToDownload?.pdfObjectUrl}
                    version={versionToDownload?.version}
                />
            </Col>
        </Row>
    )
}

const mapStatetoProps = (state) => {
    const { user } = state.Login;

    return {
        user,
        organization: state.Organization,
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        ListVersions
    )
);